import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import useAgencies from '@hooks/useAgencies'
import { generateUrls } from '@hooks/useAgency'
import useAgencySelector from '@hooks/useAgencySelector'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import AgencyListItem from '../AgencyListItem'

import type { PageContextPayload } from '../../index'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
  ]
}

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const agenciesPayload = useAgencies({
    filters: {
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: {},
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencies,
    filteredAgenciesCount,
    hasFilteredAgencies,
    loading,
  } = agenciesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { selectedAgencyId } = useAgencySelector()
  const { agenciesIndexUrl } = generateUrls({}, { prefixRoute: `agencies/${selectedAgencyId}/manage` })

  return (
    <>
      <Helmet>
        <title>Agencies | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="current"
        nestedNavigation
        tabBarItems={[
          {
            href: agenciesIndexUrl,
            icon: faCheckCircle,
            key: 'current',
            title: 'Agencies',
          },
        ]}
        title="Agencies"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAgencies && <ListBlankState />}

            {!loading && hasFilteredAgencies && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Agencies">
                <Box flexDirection="column">
                  {filteredAgencies.map(agency => (
                    <AgencyListItem
                      key={agency.id}
                      agency={agency}
                    />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredAgenciesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
